import { z } from 'zod';
import { emailSchema, idSchema, nameSchema, passwordSchema, turnstileTokenSchema } from './common.js';

export const loginSchema = z
  .object({
    email: emailSchema,
    password: z.string().min(1, 'Password is required').max(128),
    turnstileToken: turnstileTokenSchema,
  })
  .strict();

export type LoginInput = z.infer<typeof loginSchema>;

export const passwordResetRequestSchema = z
  .object({
    email: emailSchema,
    turnstileToken: turnstileTokenSchema,
  })
  .strict();

export const passwordResetCompleteSchema = z
  .object({
    token: z.string().min(20).max(256),
    newPassword: passwordSchema,
    /**
     * Cloudflare Turnstile token. OPTIONAL bot-gate: the single-use,
     * 60-minute reset token is the primary access control, so Turnstile is
     * defense-in-depth here and is verified only when supplied. It is kept
     * optional (not required) because the same schema backs the candidate
     * activate flow (`signInAfter`), whose landing page does not render a
     * Turnstile widget — requiring it would break activation. Clients that
     * do render the widget (e.g. the password-reset form) still get the
     * extra check.
     */
    turnstileToken: turnstileTokenSchema.optional(),
    /**
     * When `true`, the server creates a fresh session for the user
     * immediately after resetting the password and returns the
     * session token in the response. Used by the activate flow so
     * candidates can land straight on their aptitude test without
     * a separate sign-in step.
     */
    signInAfter: z.boolean().optional(),
  })
  .strict();

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1).max(128),
    newPassword: passwordSchema,
  })
  .strict()
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: 'New password must differ from current password',
    path: ['newPassword'],
  });

export const otpRequestSchema = z
  .object({
    email: emailSchema,
    turnstileToken: turnstileTokenSchema,
  })
  .strict();

export const otpVerifySchema = z
  .object({
    email: emailSchema,
    code: z
      .string()
      .length(6, 'OTP code must be 6 digits')
      .regex(/^[0-9]{6}$/u, 'OTP code must be digits only'),
    /**
     * Cloudflare Turnstile token. OPTIONAL bot-gate, verified only when
     * supplied. The per-email/per-IP rate limit on this endpoint (plus the
     * per-OTP 5-attempt cap) is the primary brute-force defense; a second
     * Turnstile challenge moments after the one on `/otp/request` adds
     * friction for little gain and the verify step is reached only after a
     * successful request. Clients may still submit a token for the extra check.
     */
    turnstileToken: turnstileTokenSchema.optional(),
  })
  .strict();

export const passkeyRegistrationVerifySchema = z
  .object({
    response: z.unknown(),
  })
  .strict();

export const passkeyLoginVerifySchema = z
  .object({
    response: z.unknown(),
  })
  .strict();

export const totpEnrollVerifySchema = z
  .object({
    code: z.string().length(6).regex(/^[0-9]{6}$/u),
  })
  .strict();

export const totpVerifySchema = z
  .object({
    mfaToken: z.string().min(20).max(256),
    code: z.string().length(6).regex(/^[0-9]{6}$/u),
  })
  .strict();

export const roleSchema = z.enum([
  'super_admin',
  'academic_admin',
  'finance_admin',
  'nalim',
  'teacher',
  'parent',
  'student',
  'support_viewer',
  // Roles below exist in the role catalog but were not previously surfaced
  // in the Zod enum. They're additive: no existing match-on-role check in
  // the codebase enumerates the full set, so widening here cannot regress
  // behaviour. See `packages/db/src/seed/dev.ts` for the canonical rows.
  'candidate',
  'hr',
]);

export type Role = z.infer<typeof roleSchema>;

export const userInviteSchema = z
  .object({
    email: emailSchema,
    displayName: nameSchema,
    primaryRole: roleSchema,
    link: z
      .object({
        type: z.enum(['teacher', 'manager', 'student']),
        id: idSchema,
      })
      .optional(),
    householdId: idSchema.optional(),
  })
  .strict();
export type UserInviteInput = z.infer<typeof userInviteSchema>;

export const userUpdateSchema = z
  .object({
    displayName: nameSchema.optional(),
    primaryRole: roleSchema.optional(),
  })
  .strict();

/**
 * Link an existing teacher / manager / student profile to a user account
 * that was created without a profile link at invite time. Used by
 * `POST /api/users/:id/link-profile`.
 */
export const userLinkProfileSchema = z
  .object({
    type: z.enum(['teacher', 'manager', 'student']),
    id: idSchema,
  })
  .strict();
export type UserLinkProfileInput = z.infer<typeof userLinkProfileSchema>;

/**
 * Admin-set-password input. Used by `POST /api/users/:id/set-password`.
 * Same password rules as the normal user-set path. The caller is the
 * super_admin choosing a password ON BEHALF of the target user; the
 * server forces `must_reset_password=true` so the user is required to
 * change it on first login.
 */
export const userSetPasswordSchema = z
  .object({
    newPassword: passwordSchema,
  })
  .strict();
export type UserSetPasswordInput = z.infer<typeof userSetPasswordSchema>;
export type UserUpdateInput = z.infer<typeof userUpdateSchema>;

/**
 * Pagination + search for `GET /api/users`.
 *
 * `status`, `role`, `lastLoginFrom`, `lastLoginTo`, and `sort` are deliberately
 * NOT validated here: they are multi-select / range / sort facets parsed by the
 * route's shared list-filter helpers (`parseStatusListFilter`,
 * `parseEnumListFilter`, `parseDateOnly`, `parseSort`) straight off the raw
 * query string. Routing a comma-list (`status=active,inactive`) through a
 * single-value `z.enum` would 400 before the helper ever ran, so those facets
 * own their own validation. Only the always-single params live in the schema so
 * `q` keeps its exact existing trim + length contract.
 */
export const userListQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    q: z.string().trim().min(1).max(120).optional(),
  })
  .strict();
export type UserListQueryInput = z.infer<typeof userListQuerySchema>;

/** Account language. Mirrors the `users.locale` enum (migration 0028). */
export const localeSchema = z.enum(['en', 'ml']);
export type Locale = z.infer<typeof localeSchema>;

export const meResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string(),
  roles: z.array(roleSchema).min(1),
  primaryRole: roleSchema,
  /**
   * UI + notification language for this account (`users.locale`).
   *
   * Optional here, not because the API omits it, but because this schema is a
   * plain `z.object()` — an unknown key would be STRIPPED rather than rejected,
   * so a field missing from this list silently disappears from any consumer
   * that validates through it. That is how the mobile client's own hand-copied
   * MeResponse drifted. Declaring it keeps the contract honest.
   */
  locale: localeSchema.optional(),
  mfaEnrolled: z.boolean(),
  passkeyEnrolled: z.boolean(),
  householdId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
});

export type MeResponse = z.infer<typeof meResponseSchema>;
